import { forwardRef } from 'react'
import agency from '../../../api/agency/hooks'
import FormCheckbox from './FormCheckbox'
import FormLine from './FormLine'
import { contactSection, descriptionSection, footerSection, generalInfoSection, locationSection, priceSection, profileSection } from './sections'

const PrintContent = forwardRef<HTMLDivElement>((_, ref) => {
    const { data: agencyData } = agency.GetAgency.useQuery()

    return (
        <main ref={ref}>
            {/* PAGE 1 */}
            <section>
                {/* Agency info */}
                <header className={`d-flex justify-content-${agencyData?.logo ? 'around' : 'end'} align-items-center bg-white mb-2 p-3`}>
                    {agencyData?.logo ? (
                        <img
                            src={agencyData.logo}
                            alt="Agency Logo"
                            style={{
                                maxHeight: '100px',
                                width: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    ) : null}
                    <div className="fs-10">
                        <div>{`${agencyData?.name}, ${agencyData?.street} ${agencyData?.streetNumber}`}</div>
                        <div>{`${agencyData?.zipCode} ${agencyData?.city}, ${agencyData?.country}; tel: ${agencyData?.phoneNumber}`}</div>
                        <div>{`Email: ${agencyData?.email}`}</div>
                        <div>{`Web: ${agencyData?.websiteUrl || ''}`}</div>
                    </div>
                </header>

                {/* Property Profile */}
                <div className="mb-2 mx-2 p-3 border">
                    <h5 className="fw-bold mb-2">Profil:</h5>
                    <div className="d-flex justify-content-start align-items-center">
                        {profileSection.services.map((service) => (
                            <FormCheckbox key={service.field} item={service} />
                        ))}
                    </div>
                    <div className="d-flex align-items-center">
                        {profileSection.types.map((type) => (
                            <FormCheckbox key={type.field} item={type} />
                        ))}
                    </div>
                    <FormLine item={profileSection.subTypes} />
                </div>

                {/* Location Information */}
                <div className="mb-2 mx-2 p-3 border">
                    <h5 className="fw-bold mb-2">Lokacija:</h5>
                    <div className="d-flex flex-wrap">
                        {locationSection.map((location) => {
                            const lineWidth = location.shortLine ? 'w-25' : 'w-50'
                            return <FormLine key={location.field} item={location} className="w-50" lineClassName={lineWidth} />
                        })}
                    </div>
                </div>

                {/* Price Information */}
                <div className="mb-2 mx-2 p-3 border">
                    <h5 className="fw-bold mb-2">Cijena:</h5>
                    <div className="d-flex flex-wrap">
                        {priceSection.line.map((price) => {
                            const widthClass = price.isRent ? 'w-100' : 'w-50'
                            return <FormLine key={price.field} item={price} className={widthClass} />
                        })}
                    </div>
                    <div className="d-flex">
                        {priceSection.checkbox.sale.map((price) => (
                            <FormCheckbox key={price.field} item={price} />
                        ))}
                    </div>
                    <div className="d-flex">
                        {priceSection.checkbox.rent.map((price) => (
                            <FormCheckbox key={price.field} item={price} />
                        ))}
                    </div>
                </div>

                {/* General Information - Part 1 */}
                <div className="mb-2">
                    <div className="mb-2 mx-2 p-3 border">
                        <h5 className="fw-bold mb-2">Ostale informacije:</h5>
                        <div className="d-flex flex-wrap">
                            {generalInfoSection.area.map((info) => {
                                const divWidth = info.fullWidth ? 'w-100' : 'w-50'
                                return <FormLine key={info.field} item={info} className={divWidth} lineClassName="w-25" />
                            })}
                        </div>
                    </div>
                    <div className="d-flex flex-wrap mb-2 mx-2 p-3 border">
                        {generalInfoSection.land.map((info) => (
                            <FormLine key={info.field} item={info} className="w-50" lineClassName="w-25" />
                        ))}
                    </div>
                    <div className="d-flex flex-wrap mb-2 mx-2 p-3 border">
                        {generalInfoSection.rooms.map((info) => (
                            <FormLine key={info.field} item={info} className="w-50" lineClassName="w-25" />
                        ))}
                    </div>
                </div>
            </section>

            {/* PAGE 2 */}
            <section className="page-break-before page-break-after">
                {/* General Information - Part 2 */}
                <div className="d-flex flex-wrap mb-2 mx-2 p-3 border">
                    {generalInfoSection.utilities.map((info) => {
                        const divWidth = info.fullWidth ? 'w-100' : 'w-50'
                        const lineWidth = info.longLine ? 'w-50' : 'w-25'
                        return <FormLine key={info.field} item={info} className={divWidth} lineClassName={lineWidth} />
                    })}
                </div>

                <div className="d-flex flex-wrap mb-2 mx-2 p-3 border">
                    {generalInfoSection.distance.map((info) => (
                        <FormLine key={info.field} item={info} className="w-50" lineClassName="w-25" />
                    ))}
                </div>

                <div className="d-flex flex-wrap mb-2 mx-2 p-3 border">
                    {generalInfoSection.details.map((info) => {
                        const divWidth = info.fullWidth ? 'w-100' : 'w-50'
                        const lineWidth = info.shortLine ? 'w-25' : 'w-50'
                        return <FormLine key={info.field} item={info} className={divWidth} lineClassName={lineWidth} />
                    })}
                </div>

                <div className="mx-2 mb-2 p-3 border">
                    <div className="fs-12 mb-2">{generalInfoSection.propertySpace.title.field}:</div>
                    <div className="d-flex flex-wrap">
                        {generalInfoSection.propertySpace.line.map((info) => {
                            return <FormLine key={info.field} item={info} lineClassName="w-25" isPropertySpace={true} />
                        })}
                    </div>
                </div>

                {/* Description Section */}
                <div className="mb-2 mx-2 p-3 border">
                    <h5 className="fw-bold mb-2">Opis:</h5>
                    <div className="d-flex flex-wrap">
                        {descriptionSection.map((description) => {
                            return description.field === 'Naslov' ? (
                                <FormLine key={description.field} item={description} className="w-100" lineClassName="w-50" />
                            ) : (
                                <div key={description.field} className="w-100">
                                    <div className="fs-12 mb-5">{description.field}:</div>
                                    <div className="w-100 mb-5 border-bottom border-primary"></div>
                                    <div className="w-100 mb-5 border-bottom border-primary"></div>
                                    <div className="w-100 mb-5 border-bottom border-primary"></div>
                                    <div className="w-100 border-bottom border-primary"></div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* PAGE 3 */}
            <section className="page-break-before">
                {/* Contact Information */}
                <div className="mb-2">
                    <div className="mx-2 mb-2 p-3 border">
                        <h5 className="fw-bold mb-2">Podaci o vlasniku nekretnine:</h5>
                        <div className="d-flex flex-wrap">
                            {contactSection.main.map((contact) => {
                                return <FormLine key={contact.field} item={contact} className="w-50" lineClassName="w-50" />
                            })}
                        </div>
                    </div>
                    <div className="mx-2 p-3 border">
                        <div className="d-flex flex-wrap">
                            {contactSection.other.map((contact) => (
                                <FormLine key={contact.field} item={contact} className="w-50" lineClassName="w-50" />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer Information */}
                <footer className="mb-2 mx-2 p-3 border">
                    <div className="d-flex flex-wrap">
                        {footerSection.map((item) => (
                            <FormLine key={item.field} item={item} className="w-50" lineClassName="w-50" />
                        ))}
                    </div>
                </footer>
            </section>
        </main>
    )
})

export default PrintContent
